import jsPDF from 'jspdf';
import { generatePixPayload, generatePixQrCodeDataUrl, isPixKeyEncodable } from '@/lib/pixQrCode';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { formatDocument, detectDocumentKind } from '@/lib/documentUtils';
import { unitShortLabel } from '@/lib/units';
import { formatQuantityDisplay } from '@/lib/quantity';
import {
  computeQuoteTotals,
  hasTotalsBreakdown,
  quoteItemLineTotal,
  type QuoteTotals,
} from '@/lib/quoteTotals';
import { acceptsPix, formatPaymentMethods } from '@/lib/quotePaymentInfo';
import { formatIssueLine } from '@/lib/quoteHeading';
import type { PixQrMode } from '@/lib/paymentDetection';
import { isChargePaid, isChargePayable } from '@/lib/asaasCharge';
import { ASAAS_PDF_DISCLOSURE } from '@/lib/asaasDisclosure';
import { diagnosisEntries, type ServiceOrderDiagnosis } from '@/lib/serviceOrderDiagnosis';
import { assetEntries, type ServiceOrderAsset } from '@/lib/serviceOrderAsset';
// Re-export: o `compacto` monta a lista densa dele e precisa das entradas
// preenchidas, mas todos os templates importam só de './shared'.
export { diagnosisEntries, assetEntries };

// Re-export para os templates usarem sem importar documentUtils diretamente.
export { formatDocument, detectDocumentKind };
// Idem para o rótulo das formas aceitas: o `compacto` monta o próprio bloco de
// observações (não usa `renderPaymentSection`) e precisa formatar na mão.
export { formatPaymentMethods };

/**
 * Rótulo do documento do cliente para o PDF: "CPF: 000.000.000-00" ou
 * "CNPJ: 00.000.000/0000-00". Retorna '' quando não há documento.
 */
export const clientDocumentLabel = (document?: string | null): string => {
  if (!document) return '';
  const kind = detectDocumentKind(document);
  const prefix = kind === 'cnpj' ? 'CNPJ' : kind === 'cpf' ? 'CPF' : 'Doc';
  return `${prefix}: ${formatDocument(document)}`;
};

// ============================================================================
// TYPES
// ============================================================================

export type PdfTemplateName =
  | 'classico'
  | 'minimalista'
  | 'corporativo'
  | 'moderno'
  | 'elegante'
  | 'compacto'
  | 'botanico'
  | 'onda'
  | 'tecnico'
  /**
   * Escopo do serviço SEM nenhum preço — para aprovar o trabalho antes de
   * discutir valor, ou para o cliente repassar sem expor quanto paga.
   */
  | 'proposta';

export const PDF_TEMPLATE_NAMES: PdfTemplateName[] = [
  'classico',
  'minimalista',
  'corporativo',
  'moderno',
  'elegante',
  'compacto',
  'botanico',
  'onda',
  'tecnico',
  'proposta',
];

export interface PDFQuoteItem {
  name: string;
  description?: string | null;
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: 'value' | 'percent' | null;
  unit?: string | null;
  image_url?: string | null;
}

export interface PDFClientInfo {
  name: string;
  phone?: string | null;
  address?: string | null;
  /** CPF ou CNPJ do cliente (só dígitos). Exibido no Recibo/PDF quando presente. */
  document?: string | null;
}

export interface PDFCompanyInfo {
  name: string;
  cnpj?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  logoUrl?: string | null;
  pixKey?: string | null;
  pixCity?: string | null;
}

export interface PDFGeneralPhoto {
  imageUrl: string;
  title?: string;
  description?: string;
}

/**
 * A cobrança Pix DINÂMICA deste documento — uma linha de `asaas_charges`.
 *
 * Existe só no modo `'dynamic'`. `value` viaja junto de propósito: é o valor que
 * o cliente vai efetivamente pagar, e é ele que precisa bater com o total
 * impresso (ver `dynamicPixIsUsable`).
 */
export interface PdfPixDynamic {
  /** `asaas_charges.pix_payload` — o BR Code "copia e cola" da cobrança. */
  payload: string;
  /** `asaas_charges.pix_qr_base64` — PNG em base64, SEM o prefixo `data:`. */
  qrBase64?: string | null;
  /** `asaas_charges.value`. */
  value: number;
  /**
   * `asaas_charges.status`. OBRIGATÓRIO, e o `tsc` é quem cobra: um call-site
   * que o esquecesse imprimiria o QR de uma cobrança já paga, e o cliente
   * pagaria de novo. Ver o cabeçalho de `src/lib/asaasCharge.ts`.
   */
  status: string;
}

export interface PDFQuoteData {
  documentType: 'orcamento' | 'pedido' | 'recibo' | 'ordem_servico' | 'contrato';
  /**
   * `quotes.title` — o título que o PRESTADOR deu ao documento. Quando existe,
   * ele SUBSTITUI a palavra do tipo no cabeçalho ("ORÇAMENTO"), no mesmo lugar
   * e no mesmo tamanho; quando falta, o cabeçalho é o de sempre, byte a byte.
   *
   * Quem resolve é `resolveDocumentTitle` — nenhum template pode ler este campo
   * direto, senão a precedência vira 11 implementações que divergem na primeira
   * correção. Ausência (`null`/`undefined`/só espaços) repete o passado: é a
   * mesma disciplina de `payment_methods` (#73) e da garantia (#104).
   */
  title?: string | null;
  /**
   * `quotes.subtitle` — a linha secundária, desenhada logo abaixo do cabeçalho,
   * menor e em tom suave. Ausente (`null`/`undefined`/só espaços) não desenha
   * NADA e não move `yPos` nenhum, que é o que mantém todo PDF já emitido
   * idêntico. Quem decide é `resolveDocumentSubtitle`, nunca o template.
   */
  subtitle?: string | null;
  /**
   * `quotes.issue_place` — a cidade da linha "São Paulo, 9 de agosto de 2026",
   * impressa por `renderSignatureSection` logo acima da assinatura.
   *
   * ⚠️ Sem cidade a linha inteira some, **inclusive a data**: imprimir só a
   * data acrescentaria uma linha nova em todo documento do histórico. Ver
   * `formatIssueLine` em `src/lib/quoteHeading.ts`.
   */
  issuePlace?: string | null;
  client: PDFClientInfo;
  company?: PDFCompanyInfo;
  quoteDate: string;
  validityDays?: number | null;
  warranty?: string | null;
  notes?: string | null;
  termsConditions?: string | null;
  /**
   * Diagnóstico técnico da OS (`service_orders.defect/diagnosis/solution`).
   * Só a Ordem de Serviço preenche; ausente em todos os outros documentos, e
   * `renderDiagnosisSection` não desenha nada quando não há conteúdo — é isso
   * que mantém o PDF do histórico byte a byte igual.
   */
  diagnosis?: ServiceOrderDiagnosis | null;
  /**
   * Equipamento/objeto atendido (`service_orders.asset_*`). Como o diagnóstico:
   * só a OS preenche, e `renderServiceOrderSections` não desenha nada quando não
   * há conteúdo — é o que mantém o PDF do histórico byte a byte igual.
   */
  serviceOrderAsset?: ServiceOrderAsset | null;
  /**
   * Responsáveis e prazos da OS. Todos opcionais e todos omitidos quando
   * ausentes (nunca "Técnico: —"), pela mesma razão do bloco acima.
   */
  serviceOrderInfo?: {
    attendantName?: string | null;
    technicianName?: string | null;
    /** ISO COM HORA — `service_orders.expected_date` (previsão de entrega). */
    expectedDate?: string | null;
    /** ISO COM HORA — `service_orders.completed_at` (encerramento). */
    completedAt?: string | null;
  } | null;
  items: PDFQuoteItem[];
  /**
   * O que o cliente PAGA — já com frete e desconto de cabeçalho. Continua sendo
   * o número que os templates imprimem como TOTAL, e é o mesmo `quotes.total`
   * gravado no banco. Os três campos abaixo existem para DECOMPOR esse número no
   * documento; quem os omite imprime só a linha de total, como sempre foi.
   */
  total: number;
  /** `quotes.shipping` — somado por último, sem desconto. */
  shipping?: number | null;
  /** `quotes.discount` — desconto do documento, sobre o subtotal já líquido. */
  discount?: number | null;
  /** `quotes.discount_type` — `value` | `percent`. */
  discountType?: 'value' | 'percent' | null;

  // === Pagamento e referência (campos de EXIBIÇÃO) =========================
  /**
   * `quotes.payment_methods` — as formas ACEITAS. Impressas por
   * `renderPaymentSection` e, o que é menos óbvio, é este campo que decide se o
   * bloco de QR PIX aparece:
   *
   *   `null`/ausente → LEGADO: mostra o QR como sempre mostrou
   *   `[]`           → recusa explícita: esconde
   *   inclui `pix`   → mostra
   *
   * A regra e o porquê estão em `src/lib/quotePaymentInfo.ts`. O ponto
   * essencial: **ausência nunca é recusa** — senão todo PDF já emitido perderia
   * o QR de um dia para o outro.
   */
  paymentMethods?: readonly string[] | null;
  /** `quotes.payment_terms` — condições em texto livre. */
  paymentTerms?: string | null;
  /** `quotes.reference` — referência EXTERNA do cliente (OC, contrato, chamado). */
  reference?: string | null;

  /**
   * O modo de QR Pix já RESOLVIDO por `resolvePixQrMode`
   * (`src/lib/paymentDetection.ts`). Ausente = `'static'` = o comportamento de
   * sempre, e é por isso que todo PDF do histórico sai byte a byte igual.
   *
   * O template NÃO resolve o modo: ele depende do plano, do interruptor da
   * empresa e do estado da subconta, nenhum dos quais o gerador de PDF enxerga.
   */
  pixQrMode?: PixQrMode;
  /**
   * A cobrança Asaas deste documento, quando o modo é `'dynamic'`. Ausente no
   * modo dinâmico significa "a cobrança ainda não foi criada" — e aí o
   * documento sai SEM QR nenhum, deliberadamente (ver `pixQrEligible`).
   */
  pixDynamic?: PdfPixDynamic | null;
  isPremium: boolean;
  brandColorRGB?: [number, number, number];
  sequenceId?: number | null;
  showSequenceId?: boolean;
  generalPhotos?: PDFGeneralPhoto[];
  template?: PdfTemplateName;

  // === Assinaturas ========================================================
  // Usadas em DOIS contextos diferentes, com o mesmo par de campos:
  //  • contrato — as duas assinaturas são atos jurídicos datados, desenhadas
  //    lado a lado por `contrato.ts` junto do rodapé de auditoria;
  //  • orçamento/pedido/OS — `renderSignatureSection` desenha a imagem ACIMA da
  //    linha correspondente. `providerSignatureDataUrl` vem da assinatura salva
  //    da empresa (`companies.signature_url`, PRO) e `clientSignatureDataUrl`
  //    da aprovação pública (`quote_approvals.signature`). Ambos aceitam data
  //    URL ou URL do Storage privado (`getDataUrl` assina/lê do cache).
  // Ausentes → as linhas em branco de sempre.
  /** Assinatura do prestador (data URL PNG ou URL do Storage). */
  providerSignatureDataUrl?: string | null;
  /** Assinatura do cliente/tomador (data URL PNG ou URL do Storage). */
  clientSignatureDataUrl?: string | null;
  providerSignerName?: string | null;
  clientSignerName?: string | null;
  /** ISO da assinatura do cliente (no orçamento, o instante da aprovação). */
  clientSignedAt?: string | null;

  // === Contrato (documentType === 'contrato') =============================
  // O corpo do contrato (texto multi-parágrafo) substitui a tabela de itens; o
  // rodapé de auditoria só aparece no PDF de contrato. Campos ignorados nos
  // demais documentos.
  /** Corpo do contrato (parágrafos separados por \n). */
  contractContent?: string | null;
  /** Documento do cliente signatário (só dígitos; mascarado ao imprimir). */
  clientSignerDocument?: string | null;
  /** ISO da assinatura do prestador. */
  providerSignedAt?: string | null;
  /** IP do cliente no ato da assinatura (trilha de auditoria). */
  auditIp?: string | null;
  /** SHA-256 do conteúdo assinado (tamper-evidence). */
  auditContentSha256?: string | null;

  // === Recibo (documentType === 'recibo') =================================
  // O recibo é um bloco único (não tabela): valor em destaque, a frase "Recebi
  // de [pagador] a importância de [valor por extenso] referente a [descrição]",
  // forma de pagamento, local/data e a assinatura opcional do EMITENTE. Campos
  // ignorados nos demais documentos. `total` carrega o valor do recibo.
  /** "referente a" — descrição do que foi pago. */
  receiptDescription?: string | null;
  /** Forma de pagamento (rótulo pt-BR, ex.: "PIX"). */
  /**
   * `quotes.extra_fields` — pares rótulo/valor criados pelo prestador.
   * Ausente/vazio não desenha nada. Ver `src/lib/quoteExtraFields.ts`.
   */
  extraFields?: Array<{ label: string; value: string }> | null;
  receiptPaymentMethodLabel?: string | null;
  /** `receipts.payment_terms` — como o pagamento foi combinado ("3x sem juros"). */
  receiptPaymentTerms?: string | null;
  /** Local de emissão (cidade). */
  receiptIssuePlace?: string | null;
  /** Observações adicionais impressas abaixo do corpo. */
  receiptNotes?: string | null;
  /** Assinatura do emitente (data URL PNG), opcional. */
  issuerSignatureDataUrl?: string | null;
  issuerSignerName?: string | null;

  /**
   * `companies.pdv_signature_line` — se o documento do PDV leva a linha de
   * assinatura do CLIENTE. Só tem efeito em `documentType === 'pedido'`; nos
   * demais documentos a linha é do fluxo (aprovação de orçamento, entrega de
   * OS) e não é configurável. Ver {@link showsPdvClientSignature}.
   */
  pdvSignatureLine?: boolean | null;

  /**
   * `companies.logo_watermark` — imprime a logo como marca-d'água atrás do
   * conteúdo. Ausente/false = o documento de sempre, sem carimbo. Só vale para
   * o A4; ver {@link renderLogoWatermark}.
   */
  logoWatermark?: boolean | null;
}

/**
 * O documento do PDV leva a linha "Assinatura do cliente"?
 *
 * FONTE ÚNICA da regra — o A4 (`renderSignatureSection`) e o cupom térmico
 * consultam esta função. Com a condição escrita duas vezes, desligar a
 * assinatura tiraria a linha de um formato e deixaria no outro, e o usuário
 * veria o cupom limpo e o PDF com a linha, sem erro nenhum.
 *
 * A ausência (`undefined`/`null`) vale TRUE de propósito: é o estado de todo
 * documento gerado antes desta opção existir e de todo chamador que não passa o
 * campo. Ler ausência como recusa apagaria a linha de quem nunca abriu a
 * configuração (mesma regra do QR de PIX e do custo nulo).
 */
export const showsPdvClientSignature = (data: PDFQuoteData): boolean => {
  if (data.documentType !== 'pedido') return true;
  return data.pdvSignatureLine !== false;
};

export type RGB = [number, number, number];

// ============================================================================
// COLOR / DATE / MONEY HELPERS
// ============================================================================

export const hexToRgb = (hex: string): RGB => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
  }
  return [16, 185, 129];
};

/**
 * Mistura a cor com BRANCO: `f = 0` devolve a cor, `f = 1` devolve branco.
 * Os templates novos derivam fundos/faixas claras da cor da marca com isto em
 * vez de usar transparência — jsPDF desenha por cima do que já existe, então a
 * cor "clara" precisa ser opaca e pré-misturada.
 */
export const tintColor = (color: RGB, f: number): RGB =>
  color.map((v) => Math.round(v + (255 - v) * f)) as unknown as RGB;

/** Mistura a cor com PRETO: `f = 0` devolve a cor, `f = 1` devolve preto. */
export const shadeColor = (color: RGB, f: number): RGB =>
  color.map((v) => Math.round(v * (1 - f))) as unknown as RGB;

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

// Split date string safely (avoids timezone issues)
export const formatQuoteDate = (quoteDate?: string): string => {
  if (!quoteDate) {
    return new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  }
  const datePart = quoteDate.split('T')[0];
  const [ano, mes, dia] = datePart.split('-');
  return `${dia}/${mes}/${ano}`;
};

export const calcValidityDate = (
  quoteDate: string,
  validityDays: number,
): string => {
  const datePart = quoteDate.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day + validityDays);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// ============================================================================
// UNIT & DOCUMENT LABELS
// ============================================================================

/**
 * @deprecated Use {@link unitShortLabel} (src/lib/units.ts).
 *
 * Mantido só como referência do que este arquivo conhecia antes: meia dúzia de
 * códigos em inglês/português e, para todo o resto, um corte nos 3 primeiros
 * caracteres — que transformava `hr_homem` em "hr_" e `lauda` em "lau". O
 * vocabulário completo (e a tradução dos códigos antigos) agora é único.
 */
export const unitMap: Record<string, string> = {
  unit: 'un',
  unidade: 'un',
  hour: 'h',
  hora: 'h',
  day: 'd',
  dia: 'd',
  meter: 'm',
  metro: 'm',
  service: 'sv',
  servico: 'sv',
  serviço: 'sv',
  package: 'pct',
  pacote: 'pct',
  box: 'cx',
  caixa: 'cx',
  kg: 'kg',
  kilogram: 'kg',
  quilograma: 'kg',
};

export const documentTypeLabels: Record<string, string> = {
  orcamento: 'ORÇAMENTO',
  proposta_comercial: 'PROPOSTA COMERCIAL',
  cotacao: 'COTAÇÃO',
  estimativa: 'ESTIMATIVA',
  pedido: 'PEDIDO DE VENDA',
  ordem_servico: 'ORDEM DE SERVIÇO',
  plano_trabalho: 'PLANO DE TRABALHO',
  recibo: 'RECIBO',
  comprovante: 'COMPROVANTE',
  contrato: 'CONTRATO',
};

export const getDocumentTitle = (documentType: string): string =>
  documentTypeLabels[documentType] || 'ORÇAMENTO';

/**
 * O título que vai ao CABEÇALHO do documento — a fonte única dos 11 templates.
 *
 * Precedência: título do prestador → palavra do tipo. A substituição é decisão
 * de produto (o documento passa a se anunciar pelo nome que o prestador deu), e
 * o custo aceito é que dois PDFs de tipos diferentes deixam de se distinguir
 * pelo cabeçalho quando os dois têm título.
 *
 * O caso VAZIO é o que preserva o histórico: `null`, `undefined` e string só de
 * espaços caem no rótulo do tipo, então todo PDF já emitido sai idêntico. Um
 * `?? ''` ou um `title ||` sem `trim` aqui imprimiria um cabeçalho em branco —
 * ausência não é título vazio, é ausência (#61/#73).
 *
 * O caso NÃO é normalizado para maiúsculas de propósito: o rótulo do tipo é uma
 * constante do app (e vem em caixa alta), mas o título é texto do usuário —
 * "REFORMA DA COZINHA E ÁREA DE SERVIÇO" grita, e ninguém escolheu isso.
 */
export const resolveDocumentTitle = (data: Pick<PDFQuoteData, 'documentType' | 'title'>): string =>
  data.title?.trim() || getDocumentTitle(data.documentType);

/**
 * O SUBTÍTULO do cabeçalho — `''` quando não há, e `''` significa "não desenhe".
 *
 * Existe pela mesma razão de `resolveDocumentTitle`: com 11 templates, a regra
 * "só espaços é ausência" escrita 11 vezes diverge na primeira correção. Nenhum
 * template lê `data.subtitle` direto, e há guard de varredura cobrando isso.
 *
 * Diferente do título, ele NÃO tem fallback: sem subtítulo o cabeçalho é o de
 * sempre, e é justamente isso que mantém o histórico byte a byte.
 */
export const resolveDocumentSubtitle = (data: Pick<PDFQuoteData, 'subtitle'>): string =>
  data.subtitle?.trim() || '';

/**
 * Desenha o subtítulo LOGO ABAIXO do título e devolve quanto o `yPos` andou.
 *
 * ─── O CONTRATO, que é o que torna isto seguro em 11 templates ──────────────
 *
 * `y` é a linha-base do TÍTULO que o template acabou de desenhar; o subtítulo
 * é desenhado em `y + gap` e a função devolve **`gap`** — ou **zero** quando
 * não há subtítulo. Assim o template escreve UMA linha:
 *
 *     yPos += renderDocumentSubtitle(doc, data, { x, y: yPos, maxWidth, color });
 *
 * e todo o resto do template continua medindo a partir do `yPos` como sempre
 * mediu. Sem subtítulo o avanço é 0 e **nenhuma coordenada muda**, que é o que
 * mantém cada PDF já emitido byte a byte igual (a mesma disciplina do #202).
 *
 * Devolver o avanço em vez de mexer no acumulador do chamador é obrigatório:
 * jsPDF não tem layout automático (#13) e cada template tem guardas de colisão
 * próprios (assinatura #34, pagamento, fotos) medidos contra o `yPos` dele.
 *
 * O texto é CORTADO, nunca quebrado em duas linhas — mesma razão do
 * `fitHeaderTitle`: a altura do cabeçalho tem de ser constante.
 */
export function renderDocumentSubtitle(
  doc: jsPDF,
  data: Pick<PDFQuoteData, 'subtitle'>,
  options: {
    /** X do subtítulo (normalmente o mesmo do título). */
    x: number;
    /** Linha-base do TÍTULO — a função desenha em `y + gap`. */
    y: number;
    maxWidth: number;
    color: RGB;
    align?: 'left' | 'right' | 'center';
    size?: number;
    /** Distância entre a linha-base do título e a do subtítulo. */
    gap?: number;
    font?: string;
    style?: string;
  },
): number {
  const subtitle = resolveDocumentSubtitle(data);
  if (!subtitle) return 0;

  const {
    x, y, maxWidth, color,
    align = 'left', size = 9, gap = 4.6, font = 'helvetica', style = 'normal',
  } = options;

  const fonteAnterior = doc.getFont();
  const tamanhoAnterior = doc.getFontSize();
  try {
    doc.setFont(font, style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);

    let texto = subtitle;
    while (texto.length > 1 && doc.getTextWidth(`${texto}…`) > maxWidth) {
      texto = texto.slice(0, -1);
    }
    if (texto !== subtitle) texto = `${texto.trimEnd()}…`;

    doc.text(texto, x, y + gap, { align });
  } finally {
    // O `doc` é global: uma medição/escrita não pode vazar estado de fonte para
    // o resto do template (mesma razão do GState em #118).
    doc.setFont(fonteAnterior.fontName, fonteAnterior.fontStyle);
    doc.setFontSize(tamanhoAnterior);
  }

  return gap;
}

/**
 * Ajusta o título do cabeçalho à largura disponível SEM mudar a geometria da
 * página: encolhe a fonte por degraus e, no menor tamanho, corta com reticências.
 *
 * A alternativa seria quebrar em duas linhas — e ela é a armadilha: jsPDF não
 * tem layout automático (#13), então uma linha a mais empurra o `yPos` de tudo
 * que vem depois, em 11 templates com guardas de colisão próprios (assinatura,
 * pagamento, fotos). Uma linha só mantém cada template com a MESMA altura de
 * cabeçalho que sempre teve, com ou sem título.
 *
 * Devolve o tamanho a aplicar; NÃO chama `setFontSize` para o chamador poder
 * medir antes de desenhar. O tamanho corrente da fonte é restaurado, porque o
 * `doc` é global e uma medição não pode vazar estado (mesma razão do GState em
 * #118).
 */
export function fitHeaderTitle(
  doc: jsPDF,
  text: string,
  maxWidth: number,
  baseSize: number,
  options: { minSize?: number; charSpace?: number } = {},
): { text: string; size: number } {
  const { minSize = Math.max(8, Math.round(baseSize * 0.55)), charSpace = 0 } = options;
  const tamanhoOriginal = doc.getFontSize();
  // `getTextWidth` NÃO conhece o `charSpace` que o `doc.text` vai aplicar — sem
  // somar o espaçamento aqui, um título medido como "coube" sai da página no
  // template que usa a opção (o botânico, com 1.5). Erro invisível: a medição é
  // honesta e o desenho é maior que ela.
  const largura = (t: string) => doc.getTextWidth(t) + charSpace * Math.max(0, t.length - 1);
  try {
    for (let size = baseSize; size >= minSize; size -= 1) {
      doc.setFontSize(size);
      if (largura(text) <= maxWidth) return { text, size };
    }

    // No menor tamanho ainda não coube: corta preservando o começo, que é o que
    // identifica o documento ("Reforma da cozinha e da área…" > "…área de serviço").
    doc.setFontSize(minSize);
    let cortado = text;
    while (cortado.length > 1 && largura(`${cortado}…`) > maxWidth) {
      cortado = cortado.slice(0, -1);
    }
    return { text: `${cortado.trimEnd()}…`, size: minSize };
  } finally {
    doc.setFontSize(tamanhoOriginal);
  }
}

// ============================================================================
// IMAGE LOADING
// ============================================================================

export const getJsPdfImageFormat = (dataUrl: string): 'PNG' | 'JPEG' => {
  const match = dataUrl.match(/^data:(image\/[a-zA-Z0-9+.-]+);base64,/);
  const mime = match?.[1]?.toLowerCase();
  if (mime === 'image/jpeg' || mime === 'image/jpg') return 'JPEG';
  return 'PNG';
};

export const getDataUrl = async (url: string): Promise<string | null> => {
  if (url.startsWith('data:')) return url;

  const signable = await resolveMediaUrl(url);
  const resolvedUrl = signable ?? url;

  // Offline, resolveMediaUrl returns a `blob:` object URL served from the
  // IndexedDB byte cache. A `data:`/`blob:` URL is already the local bytes:
  // return/fetch it directly and NEVER append a `?t=` cache-buster — a query
  // string on a blob: URL breaks the object-URL lookup and the fetch fails.
  if (resolvedUrl.startsWith('data:')) return resolvedUrl;
  const isLocalBlob = resolvedUrl.startsWith('blob:');

  const isSignedUrl =
    resolvedUrl.includes('/object/sign/') && resolvedUrl.includes('token=');
  const separator = resolvedUrl.includes('?') ? '&' : '?';
  const finalUrl = isSignedUrl || isLocalBlob
    ? resolvedUrl
    : `${resolvedUrl}${separator}t=${Date.now()}`;

  const fetchAttempts: RequestInit[] = [
    { mode: 'cors', credentials: 'omit', cache: 'no-store' },
    { mode: 'no-cors', credentials: 'omit', cache: 'no-store' },
  ];

  for (const options of fetchAttempts) {
    try {
      const response = await fetch(finalUrl, options);
      if (options.mode === 'no-cors') continue;
      if (response.ok) {
        const blob = await response.blob();
        return await new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      }
    } catch {
      // continue
    }
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => resolve(null), 15000);

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 200;
        canvas.height = img.height || 200;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } else {
          resolve(null);
        }
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(null);
    };
    img.src = finalUrl;
  });
};

export const loadLogo = async (
  logoUrl?: string | null,
): Promise<string | null> => {
  if (!logoUrl) return null;
  try {
    return await getDataUrl(logoUrl);
  } catch {
    return null;
  }
};

// ============================================================================
// MARCA-D'ÁGUA DA LOGO
// ============================================================================

/** Fração da largura útil ocupada pela marca-d'água. */
const WATERMARK_WIDTH_RATIO = 0.62;
/** Opacidade da marca-d'água. Baixa o bastante para o texto por cima continuar legível. */
const WATERMARK_OPACITY = 0.07;

/**
 * Desenha a logo da empresa como marca-d'água, centrada na página.
 *
 * ─── POR QUE ISTO É CHAMADO ANTES DE TUDO ────────────────────────────────────
 *
 * jsPDF pinta na ORDEM das chamadas e não tem z-index: o que vem depois cobre o
 * que veio antes. Uma marca-d'água desenhada no fim ficaria POR CIMA da tabela
 * de itens e do total — deixaria o documento ilegível em vez de decorá-lo. Por
 * isso cada template chama esta função como primeira instrução de desenho,
 * antes até da faixa colorida do cabeçalho.
 *
 * A opacidade vai por `GState`, e o estado é RESTAURADO no fim: sem isso, todo
 * o conteúdo desenhado depois herdaria os 7% e o PDF sairia inteiro
 * fantasmagórico — um erro que não aparece quando a marca-d'água está
 * desligada, que é o padrão, e portanto passaria batido em quase todo teste.
 *
 * Não vale para o cupom térmico: a impressora é de 1 bit e uma imagem clara ou
 * some por completo ou vira um borrão sobre o texto.
 */
/** Marca de instalação do hook, presa ao próprio documento. */
const WATERMARK_STATE = '__orcamigoWatermarkState';

interface WatermarkState {
  /** Páginas já carimbadas — o carimbo é idempotente por página. */
  stamped: Set<number>;
  stamp: () => void;
}

export const renderLogoWatermark = async (
  doc: jsPDF,
  data: PDFQuoteData,
): Promise<void> => {
  if (!data.logoWatermark) return;
  const source = data.company?.logoUrl;
  if (!source) return;

  // ─── POR QUE ISTO PRECISA SER IDEMPOTENTE ─────────────────────────────────
  //
  // A exportação de vários recibos chama o renderer do recibo N vezes sobre o
  // MESMO documento — e cada chamada instalava outra camada de hook por cima da
  // anterior. O resultado, medido no aparelho, era 2 carimbos na página 1, 3 na
  // 2 e 4 na 3: a marca-d'água ia ESCURECENDO página a página (7% empilhado
  // vira ~20%), porque as opacidades se somam. Nada falha, nada avisa — só o
  // documento fica progressivamente sujo.
  const existing = (doc as unknown as Record<string, unknown>)[WATERMARK_STATE] as
    | WatermarkState
    | undefined;
  if (existing) {
    existing.stamp();
    return;
  }

  const logo = await loadLogo(source);
  if (!logo) return;

  const format = getJsPdfImageFormat(logo);
  const gs = doc as unknown as {
    GState: new (o: { opacity: number }) => unknown;
    setGState: (s: unknown) => void;
  };

  const stamped = new Set<number>();
  const currentPage = (): number => {
    try {
      return (doc as unknown as { getCurrentPageInfo: () => { pageNumber: number } })
        .getCurrentPageInfo().pageNumber;
    } catch {
      // Sem a API, cai no número total — que é a página corrente em todo fluxo
      // deste app (nunca voltamos a uma página anterior para desenhar).
      return doc.getNumberOfPages();
    }
  };

  /** Carimba a página ATUAL, uma única vez. Síncrona de propósito — ver o hook. */
  const stampCurrentPage = (): void => {
    const page = currentPage();
    if (stamped.has(page)) return;
    stamped.add(page);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxWidth = pageWidth * WATERMARK_WIDTH_RATIO;

    let width = maxWidth;
    let height = maxWidth;
    try {
      const props = doc.getImageProperties(logo);
      if (props?.width && props?.height) {
        // Preserva a proporção e ainda respeita a altura da página: uma logo
        // muito vertical ocuparia a folha inteira se só a largura fosse limitada.
        const scale = Math.min(maxWidth / props.width, (pageHeight * 0.5) / props.height);
        width = props.width * scale;
        height = props.height * scale;
      }
    } catch {
      /* sem as dimensões, cai no quadrado do tamanho máximo */
    }

    try {
      gs.setGState(new gs.GState({ opacity: WATERMARK_OPACITY }));
      doc.addImage(logo, format, (pageWidth - width) / 2, (pageHeight - height) / 2, width, height);
    } catch {
      /* sem GState/imagem, a página simplesmente sai sem marca-d'água */
    } finally {
      // OBRIGATÓRIO: o GState é global no documento. Deixado em 7%, TODO o
      // conteúdo desenhado depois sairia fantasma — e, como a feature nasce
      // desligada, esse defeito não apareceria em quase nenhum teste.
      try {
        gs.setGState(new gs.GState({ opacity: 1 }));
      } catch {
        /* nada mais a fazer */
      }
    }
  };

  (doc as unknown as Record<string, unknown>)[WATERMARK_STATE] = {
    stamped,
    stamp: stampCurrentPage,
  } satisfies WatermarkState;

  stampCurrentPage();

  // ─── POR QUE INTERCEPTAR `addPage` ────────────────────────────────────────
  //
  // A marca-d'água tem que ser a PRIMEIRA coisa de CADA página (jsPDF pinta na
  // ordem e não tem z-index). Carimbar só aqui deixaria um documento de duas
  // páginas com a segunda em branco por trás — foi exatamente o que apareceu ao
  // conferir a amostra a olho.
  //
  // Carimbar as demais páginas no FIM não resolve: sairiam por cima do texto. E
  // pedir a cada template que carimbe depois de cada `addPage()` significaria
  // acertar ~10 pontos espalhados (os guards de colisão de assinatura, de
  // pagamento, de fotos...) e continuar errando no próximo template novo.
  //
  // Interceptar é o único ponto único: toda página nova nasce carimbada, venha
  // de onde vier. O patch morre com o documento.
  const original = doc.addPage.bind(doc);
  (doc as unknown as { addPage: typeof doc.addPage }).addPage = ((...args: unknown[]) => {
    const result = (original as (...a: unknown[]) => unknown)(...args);
    stampCurrentPage();
    return result;
  }) as typeof doc.addPage;
};

// ============================================================================
// ITEM TABLE HELPERS
// ============================================================================

export interface ItemRowResult {
  hasAnyDiscount: boolean;
  headers: string[][];
  body: string[][];
}

/**
 * Linhas da tabela de ESCOPO — descrição, quantidade e unidade, SEM dinheiro.
 *
 * Existe para o template "Proposta (sem valores)" (lacuna L4). Ela é uma função
 * própria, e não uma opção de `buildItemRows`, de propósito: o modo "sem preço"
 * de um construtor que normalmente imprime preço é o tipo de flag que alguém
 * inverte sem querer, e o defeito seria expor o valor no documento em que o
 * prestador escolheu escondê-lo — exatamente o oposto do que a feature promete.
 * Duas funções não têm como se confundir.
 */
export const buildScopeRows = (
  items: PDFQuoteItem[],
): { headers: string[][]; body: string[][] } => ({
  headers: [['DESCRIÇÃO', 'QTD', 'UN']],
  body: items.map((item) => [
    item.name || '-',
    formatQuantityDisplay(item.quantity),
    unitShortLabel(item.unit) || '-',
  ]),
});

export const buildItemRows = (items: PDFQuoteItem[]): ItemRowResult => {
  const hasAnyDiscount = items.some((item) => (item.discount || 0) > 0);

  const headers = hasAnyDiscount
    ? [['DESCRIÇÃO', 'QTD', 'UN', 'UNIT (R$)', 'DESC.', 'TOTAL (R$)']]
    : [['DESCRIÇÃO', 'QTD', 'UN', 'UNIT (R$)', 'TOTAL (R$)']];

  const body = items.map((item) => {
    const discount = item.discount || 0;
    const discountType = item.discount_type || 'value';
    // Terceira cópia da fórmula que existia no app — agora vem da fonte única
    // (src/lib/quoteTotals.ts), a mesma do formulário e dos saves. Era daqui que
    // sairia a divergência clássica: a tela somando de um jeito e o PDF de
    // outro, sem erro nenhum em lugar nenhum.
    const itemTotal = quoteItemLineTotal(item);

    const discountDisplay =
      discount > 0
        ? discountType === 'percent'
          ? `${discount}%`
          : formatCurrency(discount)
        : '-';

    // Abreviação pelo vocabulário único, que também traduz os códigos antigos
    // do catálogo (`hour` → "h") — ver src/lib/units.ts.
    const unitDisplay = unitShortLabel(item.unit) || '-';

    if (hasAnyDiscount) {
      return [
        item.name || '-',
        // Quantidade fracionária em pt-BR: `String(1.5)` imprimia "1.5", que
        // aqui se lê como mil e quinhentos.
        formatQuantityDisplay(item.quantity),
        unitDisplay,
        formatCurrency(item.unit_price),
        discountDisplay,
        formatCurrency(itemTotal),
      ];
    }
    return [
      item.name || '-',
      formatQuantityDisplay(item.quantity),
      unitDisplay,
      formatCurrency(item.unit_price),
      formatCurrency(itemTotal),
    ];
  });

  return { hasAnyDiscount, headers, body };
};

// ============================================================================
// TOTALS BREAKDOWN (shared across templates)
// ============================================================================

/**
 * A decomposição do documento, calculada UMA vez a partir dos itens + cabeçalho.
 * `total` daqui é o que os templates imprimem — o mesmo número que a tela mostra
 * e que `quotes.total` guarda, porque sai da mesma função.
 */
export const pdfQuoteTotals = (data: PDFQuoteData): QuoteTotals =>
  computeQuoteTotals({
    items: data.items,
    shipping: data.shipping,
    discount: data.discount,
    discountType: data.discountType,
  });

export interface TotalsBreakdownOptions {
  margin: number;
  pageWidth: number;
  grayText: RGB;
  /** Largura do bloco, alinhado à direita. Cada template usa a sua. */
  width: number;
}

/**
 * Linhas de Subtotal / Descontos / Frete, logo acima do bloco de TOTAL de cada
 * template. Alinhadas à direita, na largura que o template já usa para o total.
 *
 * ARMADILHA #13 (jsPDF não tem layout): cada linha desenhada AVANÇA o `yPos`, e
 * o chamador tem que usar o retorno — o bloco de total, as observações, o PIX e
 * a seção de assinatura vêm todos depois e são posicionados por coordenada.
 *
 * Quando não há nada a decompor (nenhum desconto e nenhum frete — o orçamento
 * simples, que é a maioria) a função NÃO desenha nada e devolve o `yPos`
 * intacto. É o que mantém o PDF de um orçamento simples idêntico ao histórico.
 */
export const renderTotalsBreakdown = (
  doc: jsPDF,
  totals: QuoteTotals,
  yPos: number,
  opts: TotalsBreakdownOptions,
): number => {
  // Recebe os totais JÁ CALCULADOS: o template precisa deles de qualquer forma
  // para imprimir a linha de TOTAL, e recalcular aqui percorreria os itens uma
  // segunda vez por documento.
  if (!hasTotalsBreakdown(totals)) return yPos;

  const rows: Array<[string, string]> = [['Subtotal', formatCurrency(totals.subtotal)]];
  if (totals.descontoItens > 0) {
    rows.push(['Descontos nos itens', `- ${formatCurrency(totals.descontoItens)}`]);
  }
  if (totals.descontoCabecalho > 0) {
    rows.push(['Desconto no total', `- ${formatCurrency(totals.descontoCabecalho)}`]);
  }
  if (totals.frete > 0) {
    rows.push(['Frete', `+ ${formatCurrency(totals.frete)}`]);
  }

  const { margin, pageWidth, grayText, width } = opts;
  const labelX = pageWidth - margin - width;
  const lineHeight = 4.2;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);

  let y = yPos + 3.5;
  for (const [label, value] of rows) {
    doc.text(label, labelX, y);
    doc.text(value, pageWidth - margin, y, { align: 'right' });
    y += lineHeight;
  }

  // Sobra de respiro antes do bloco de total do template.
  return y - lineHeight + 3;
};

// ============================================================================
// PAYMENT SECTION (shared across templates)
// ============================================================================

export interface PaymentSectionOptions {
  margin: number;
  pageWidth: number;
  pageHeight: number;
  grayText: RGB;
  darkText: RGB;
}

/**
 * "Formas de pagamento aceitas" + "Condições de pagamento", logo antes do bloco
 * de PIX. Compartilhada pelos 6 templates em vez de replicada — o mesmo motivo
 * de `renderTotalsBreakdown`: seis cópias de um bloco posicionado à mão viram
 * seis oportunidades de esquecer de avançar o `yPos`.
 *
 * ARMADILHA #13 (jsPDF não tem layout): o retorno é o `yPos` já avançado e o
 * chamador é OBRIGADO a usá-lo — o PIX, as fotos e a seção de assinatura vêm
 * depois, por coordenada absoluta. Quando não há nada a imprimir (o caso da
 * imensa maioria dos documentos existentes) a função devolve o `yPos` intacto
 * sem desenhar nada, e é isso que mantém o PDF de um orçamento legado
 * byte-a-byte igual ao histórico.
 */
/**
 * CAMPOS EXTRAS nomeados pelo prestador (lacuna L5) — bloco compartilhado.
 *
 * Mesmo contrato de `renderPaymentSection`: recebe o `yPos` e devolve o novo.
 * Sem campos, devolve o `yPos` INTACTO e não desenha nada — é o que mantém todo
 * documento já emitido byte a byte igual (a disciplina do #202/#104).
 *
 * ─── POR QUE É UM BLOCO PRÓPRIO, E NÃO PARTE DE "OBSERVAÇÕES" ───────────────
 *
 * O bloco de observações de cada template é guardado por
 * `if (data.warranty || data.notes)`. Pendurar os campos extras ali obrigaria a
 * mexer nessa condição em 9 templates — e esquecer UM faria os campos sumirem
 * justamente no documento que só tem eles (sem garantia e sem observações), sem
 * erro nenhum. Como bloco independente, a regra é uma só e vive aqui.
 *
 * O rótulo é do usuário, então ele é medido e o VALOR quebra em várias linhas —
 * jsPDF não tem layout automático (#13) e um valor longo escreveria por cima do
 * que estiver ao lado sem avisar.
 */
/** Os pares VÁLIDOS (meio par não é campo). Fonte única dos 3 consumidores. */
export const extraFieldEntries = (
  data: Pick<PDFQuoteData, 'extraFields'>,
): Array<{ label: string; value: string }> =>
  (data.extraFields ?? [])
    .filter((f) => f?.label?.trim() && f?.value?.trim())
    .map((f) => ({ label: f.label.trim(), value: f.value.trim() }));

export const renderExtraFields = (
  doc: jsPDF,
  data: PDFQuoteData,
  yPos: number,
  opts: {
    margin: number;
    pageWidth: number;
    pageHeight: number;
    grayText: RGB;
    darkText: RGB;
    font?: string;
  },
): number => {
  const fields = extraFieldEntries(data);
  if (fields.length === 0) return yPos;

  const { margin, pageWidth, pageHeight, grayText, darkText, font = 'helvetica' } = opts;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 4;

  let y = yPos;
  for (const field of fields) {
    doc.setFont(font, 'bold');
    doc.setFontSize(8.5);
    const label = `${field.label}: `;
    const labelWidth = doc.getTextWidth(label);

    doc.setFont(font, 'normal');
    const lines = doc.splitTextToSize(
      field.value,
      Math.max(20, contentWidth - labelWidth),
    ) as string[];

    // Quebra de página ANTES de escrever o par: o rótulo não pode ficar numa
    // folha e o valor na seguinte.
    if (y + lineHeight * lines.length > pageHeight - 40) {
      doc.addPage();
      y = 20;
    }

    doc.setFont(font, 'bold');
    doc.setTextColor(grayText[0], grayText[1], grayText[2]);
    doc.text(label, margin, y);

    doc.setFont(font, 'normal');
    doc.setTextColor(darkText[0], darkText[1], darkText[2]);
    doc.text(lines, margin + labelWidth, y);

    y += lineHeight * lines.length + 1.5;
  }

  return y + 3;
};

export const renderPaymentSection = (
  doc: jsPDF,
  data: PDFQuoteData,
  yPos: number,
  opts: PaymentSectionOptions,
): number => {
  const methodsLabel = formatPaymentMethods(data.paymentMethods);
  const terms = (data.paymentTerms || '').trim();
  if (!methodsLabel && !terms) return yPos;

  const { margin, pageWidth, pageHeight, grayText, darkText } = opts;
  const contentWidth = pageWidth - margin * 2;
  const labelWidth = 34;

  const rows: Array<[string, string]> = [];
  if (methodsLabel) rows.push(['Formas aceitas:', methodsLabel]);
  if (terms) rows.push(['Condições:', terms]);

  // Quebra ANTES de medir a altura: uma condição longa ("50% na entrada, 25% na
  // metade da obra e 25% na entrega das chaves") ocupa 2–3 linhas, e reservar
  // espaço de uma linha só faria o texto invadir o bloco de PIX abaixo.
  const wrapped = rows.map(([label, value]) => ({
    label,
    lines: doc.splitTextToSize(value, contentWidth - labelWidth) as string[],
  }));
  const lineHeight = 4;
  const blockHeight = wrapped.reduce((acc, r) => acc + r.lines.length * lineHeight, 0) + 4;

  if (yPos + blockHeight > pageHeight - 30) {
    doc.addPage();
    yPos = 20;
  }

  let y = yPos + 4;
  doc.setFontSize(8);
  for (const { label, lines } of wrapped) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text(label, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(lines, margin + labelWidth, y);
    y += lines.length * lineHeight;
  }

  return y + 2;
};

// ============================================================================
// DIAGNOSIS SECTION (Ordem de Serviço)
// ============================================================================

export interface DiagnosisSectionOptions {
  margin: number;
  pageWidth: number;
  pageHeight: number;
  grayText: RGB;
  darkText: RGB;
}

/**
 * "Diagnóstico técnico" — defeito relatado / diagnóstico / solução aplicada.
 *
 * Mesmo contrato de `renderPaymentSection`: devolve o `yPos` já avançado e o
 * chamador é OBRIGADO a usá-lo (ARMADILHA #13 — jsPDF não tem layout, tudo é
 * coordenada absoluta e o que vem depois é posicionado à mão). Quando não há
 * nada a imprimir devolve o `yPos` intacto **sem desenhar**, e é isso que
 * mantém o PDF de todo documento que não usa o bloco idêntico ao histórico.
 *
 * Os três campos são texto livre e podem ser longos (um laudo de assistência
 * técnica costuma ser), então cada um é quebrado com `splitTextToSize` ANTES de
 * medir a altura — reservar altura de uma linha faria o texto invadir o bloco
 * seguinte.
 */
export const renderDiagnosisSection = (
  doc: jsPDF,
  data: PDFQuoteData,
  yPos: number,
  opts: DiagnosisSectionOptions,
): number => {
  const entries = diagnosisEntries(data.diagnosis);
  if (entries.length === 0) return yPos;

  const { margin, pageWidth, pageHeight, grayText, darkText } = opts;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 4;

  const wrapped = entries.map((entry) => ({
    label: entry.label,
    lines: doc.splitTextToSize(entry.text, contentWidth) as string[],
  }));
  // 4 = título da seção; por bloco: 4 do rótulo + as linhas do texto + 1,5 de respiro.
  const blockHeight =
    5 + wrapped.reduce((acc, w) => acc + 4 + w.lines.length * lineHeight + 1.5, 0);

  if (yPos + blockHeight > pageHeight - 30) {
    doc.addPage();
    yPos = 20;
  }

  let y = yPos + 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('DIAGNÓSTICO TÉCNICO', margin, y);
  y += 4;

  for (const { label, lines } of wrapped) {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text(label, margin, y);
    y += 3.5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.setFontSize(8);
    doc.text(lines, margin, y);
    y += lines.length * lineHeight + 1.5;
  }

  return y + 1;
};

// ============================================================================
// SERVICE ORDER SECTIONS (equipamento + responsáveis/prazos)
// ============================================================================

export type ServiceOrderSectionsOptions = DiagnosisSectionOptions;

/**
 * "10/08/2026 14:30" no fuso de Brasília.
 *
 * ⚠️ NÃO use `formatQuoteDate` aqui: ela corta a string no `'T'`, o que é certo
 * para coluna `DATE` e ERRADO para `timestamptz` — uma previsão gravada às 22h
 * sairia impressa com a data do dia seguinte (CLAUDE.md #114).
 */
const formatTimestampBR = (iso: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Responsáveis e prazos preenchidos, na ordem do documento. Fonte ÚNICA das
 * quatro linhas — o A4 e o cupom térmico consultam esta função, senão um
 * formato ganharia um campo novo e o outro não, sem erro nenhum.
 */
export const serviceOrderInfoEntries = (
  data: Pick<PDFQuoteData, 'serviceOrderInfo'>,
): Array<{ label: string; text: string }> => {
  const info = data.serviceOrderInfo;
  if (!info) return [];

  const rows: Array<{ label: string; text: string }> = [];
  const push = (label: string, value: string | null | undefined) => {
    const text = (value ?? '').trim();
    if (text) rows.push({ label, text });
  };

  push('Atendente', info.attendantName);
  push('Técnico responsável', info.technicianName);
  push('Previsão de entrega', info.expectedDate ? formatTimestampBR(info.expectedDate) : null);
  push('Encerrada em', info.completedAt ? formatTimestampBR(info.completedAt) : null);

  return rows;
};

/**
 * Bloco de rótulo+valor com o valor quebrando em várias linhas — a mesma forma
 * de `renderExtraFields`, reaproveitada pelos dois blocos da OS.
 *
 * Devolve o `yPos` intacto e não desenha nada quando a lista está vazia
 * (ARMADILHA #13: jsPDF não tem layout, e o chamador posiciona o que vem depois
 * por coordenada absoluta — desenhar "nada" ainda assim moveria o rodapé).
 */
const renderLabeledBlock = (
  doc: jsPDF,
  title: string,
  rows: Array<{ label: string; text: string }>,
  yPos: number,
  opts: ServiceOrderSectionsOptions,
): number => {
  if (rows.length === 0) return yPos;

  const { margin, pageWidth, pageHeight, grayText, darkText } = opts;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 4;
  // Coluna do rótulo medida no MAIOR rótulo do bloco: fixá-la em pixels faria
  // "Técnico responsável" escrever por cima do valor, sem aviso nenhum.
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const labelWidth =
    Math.max(...rows.map((row) => doc.getTextWidth(`${row.label}: `))) + 1;

  const wrapped = rows.map((row) => ({
    label: row.label,
    lines: doc.splitTextToSize(row.text, Math.max(20, contentWidth - labelWidth)) as string[],
  }));
  const blockHeight = 5 + wrapped.reduce((acc, w) => acc + w.lines.length * lineHeight + 0.5, 0);

  if (yPos + blockHeight > pageHeight - 30) {
    doc.addPage();
    yPos = 20;
  }

  let y = yPos + 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text(title, margin, y);
  y += 4;

  for (let i = 0; i < wrapped.length; i++) {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text(`${rows[i].label}: `, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.setFontSize(8);
    doc.text(wrapped[i].lines, margin + labelWidth, y);
    y += wrapped[i].lines.length * lineHeight + 0.5;
  }

  return y + 1;
};

/**
 * Os dois blocos exclusivos da Ordem de Serviço: o EQUIPAMENTO recebido e os
 * RESPONSÁVEIS/prazos do atendimento.
 *
 * Uma chamada só por template de propósito. Com dois pontos de inserção seria
 * uma questão de tempo até um template ganhar um bloco e não o outro — e o
 * sintoma seria o documento sair sem o número de série justamente no template
 * que a empresa escolheu, sem erro em lugar nenhum.
 *
 * Mesmo contrato das outras seções: devolve o `yPos` já avançado, e devolve o
 * `yPos` INTACTO (sem desenhar) para qualquer documento que não seja OS ou que
 * não tenha os campos preenchidos.
 */
export const renderServiceOrderSections = (
  doc: jsPDF,
  data: PDFQuoteData,
  yPos: number,
  opts: ServiceOrderSectionsOptions,
): number => {
  if (data.documentType !== 'ordem_servico') return yPos;

  let y = renderLabeledBlock(doc, 'EQUIPAMENTO / OBJETO', assetEntries(data.serviceOrderAsset), yPos, opts);
  y = renderLabeledBlock(doc, 'RESPONSÁVEIS E PRAZOS', serviceOrderInfoEntries(data), y, opts);
  return y;
};

// ============================================================================
// PIX SECTION (shared across templates)
// ============================================================================

export interface PixSectionOptions {
  primaryColor: RGB;
  grayText: RGB;
  darkText: RGB;
  margin: number;
  pageWidth: number;
  pageHeight: number;
}

/**
 * Decide se este documento leva QR de PIX. FONTE ÚNICA da regra — o A4
 * (`renderPixSection`) e a bobina térmica consultam esta função, em vez de cada
 * um repetir os três testes. Com duas cópias, acrescentar uma condição a uma
 * delas faria um formato imprimir o QR e o outro não, sem erro nenhum.
 *
 * Os três testes, e por quê:
 *  • sem chave PIX cadastrada não há o que codificar;
 *  • `acceptsPix` distingue "não aceita" (array sem `pix`) de "não informado"
 *    (`null`, o legado, que continua mostrando o QR como sempre mostrou);
 *  • RECIBO nunca leva QR — é comprovante de pagamento JÁ FEITO, e além disso
 *    o valor dele não vem dos itens (a lista é vazia), então o QR sairia com
 *    R$ 0,00. Espelha o A4, onde `recibo.ts` sequer chama esta seção.
 */
export const pixQrEligible = (data: PDFQuoteData): boolean => {
  if (data.documentType === 'recibo') return false;
  if (!acceptsPix(data.paymentMethods)) return false;

  // ─── DOCUMENTO JÁ PAGO NÃO LEVA QR NENHUM ─────────────────────────────────
  // Nem o da cobrança (que não aceita mais pagamento), nem — e é este o ponto —
  // o ESTÁTICO. Sem esta linha, um orçamento quitado pela cobrança voltava a
  // imprimir a chave pessoal assim que o modo resolvesse como estático, o que
  // acontece sempre que não dá para confirmar a subconta: interruptor desligado
  // depois da venda, ou simplesmente o app OFFLINE. O cliente que já pagou
  // receberia um documento convidando a pagar de novo, agora num destino que o
  // app nem concilia. Medido em device (2026-08-02).
  if (data.pixDynamic && isChargePaid(data.pixDynamic.status)) return false;

  // ─── A COBRANÇA VENCE O INTERRUPTOR ───────────────────────────────────────
  // Existindo cobrança pagável, é ela que vai ao papel — mesmo que o
  // interruptor da empresa já tenha sido desligado. O motivo decisivo é a
  // página pública `/o/:id`: sem sessão, ela só enxerga a cobrança (via
  // `get_public_quote`), nunca o interruptor. Decidindo por coisas diferentes,
  // o PDF do prestador e a tela do cliente discordariam sobre COMO PAGAR.
  // Detalhe em `src/lib/asaasCharge.ts`.
  if (dynamicPixIsUsable(data)) return true;

  // Modo dinâmico e ainda sem cobrança: o documento sai SEM QR, de propósito.
  // Cair no estático seria pior — o cliente pagaria por fora, o prestador veria
  // "aguardando pagamento" para sempre, e o app estaria prometendo uma baixa
  // automática que nunca vem (#9: não anuncie um veredito que você não tem).
  // A chave pessoal também não pode aparecer: seriam dois caminhos no mesmo
  // papel, e o caminho pela chave é justamente o que o app não concilia.
  if (data.pixQrMode === 'dynamic') return false;

  // Chave presente NÃO basta: ela precisa ser codificável. A caixa de Pix é
  // desenhada antes de o QR ser construído e escreve "Escaneie o QR Code ao
  // lado para pagar" — com uma chave que `generatePixPayload` recusa (texto
  // livre, telefone estrangeiro), o cliente receberia a instrução e um espaço
  // em branco. Melhor não anunciar pagamento nenhum, e o formulário da empresa
  // avisa na origem. Ver `src/lib/pixQrCode.ts`.
  return isPixKeyEncodable(data.company?.pixKey);
};

/**
 * A cobrança dinâmica serve PARA ESTE documento? Três condições:
 *
 *  1. existe e tem BR Code;
 *  2. está **em aberto** — imprimir o QR de uma cobrança já paga faz o cliente
 *     pagar DUAS VEZES, e Pix não estorna sozinho. É o erro mais caro da
 *     feature, e não dá sinal nenhum: o QR continua tecnicamente válido depois
 *     de pago;
 *  3. cobra o MESMO valor que o documento imprime. O total do orçamento é
 *     editável depois de a cobrança nascer (mudou um item, entrou frete), e um
 *     QR com o valor antigo é a mesma divergência que `buildPixQrDataUrl` já
 *     evitava ao codificar o total CALCULADO em vez do `data.total` recebido
 *     (#69). Divergiu, o documento sai sem QR e o prestador reemite.
 */
export const dynamicPixIsUsable = (data: PDFQuoteData): boolean => {
  const dynamic = data.pixDynamic;
  if (!dynamic || typeof dynamic.payload !== 'string' || !dynamic.payload.trim()) return false;
  if (!isChargePayable(dynamic.status)) return false;
  const cents = (value: number) => Math.round(value * 100);
  return cents(dynamic.value) === cents(pdfQuoteTotals(data).total);
};

/**
 * O BR Code (Pix copia e cola) deste documento, ou `null`.
 *
 * Fonte única do CÓDIGO — o QR do PDF ({@link buildPixQrDataUrl}) e o bloco de
 * pagamento da página pública desenham a partir DESTA string. Com duas
 * construções, o "copia e cola" e o QR ao lado dele acabariam com `txid`
 * diferentes (o estático carimba `Date.now()`), e o cliente teria dois códigos
 * que não são o mesmo pagamento.
 *
 * O valor codificado é o total CALCULADO, nunca o `data.total` que o chamador
 * passou: se divergirem, o cliente escaneia e paga um número diferente do que o
 * documento diz — o pior lugar possível para uma divergência de centavos (ou de
 * um frete inteiro).
 */
export const buildPixPaymentCode = (data: PDFQuoteData): string | null => {
  if (!pixQrEligible(data)) return null;

  // Dinâmico: o payload JÁ é um BR Code pronto, emitido pelo Asaas. Não passa
  // por `generatePixPayload` — remontá-lo destruiria o `txid` da cobrança, que é
  // o que permite ao webhook reconhecer o pagamento. Ou seja: reconstruir o QR
  // "para padronizar" desligaria a conciliação sem quebrar nada visível.
  if (dynamicPixIsUsable(data) && data.pixDynamic) return data.pixDynamic.payload;

  const args = staticPixArgs(data);
  if (!args) return null;
  try {
    return generatePixPayload(args);
  } catch (payloadError) {
    // Chave irreconhecível: `generatePixPayload` LANÇA em vez de devolver um
    // código impagável (ver `src/lib/pixQrCode.ts`). Aqui degrada para "sem
    // Pix", que é o que `pixQrEligible` já promete ao não desenhar a caixa.
    console.error('Error building PIX payment code:', payloadError);
    return null;
  }
};

/**
 * Os argumentos do BR Code ESTÁTICO deste documento, num lugar só.
 *
 * Existe porque duas coisas os consomem — o QR do PDF e o "copia e cola" da
 * página pública — e escrevê-los duas vezes é como o valor, a cidade ou o nome
 * do recebedor passam a divergir entre o papel e a tela sem erro nenhum (a
 * família #62/#69).
 */
const staticPixArgs = (
  data: PDFQuoteData,
): { pixKey: string; merchantName: string; merchantCity: string; amount?: number; txId: string } | null => {
  if (!data.company?.pixKey) return null;
  const amount = pdfQuoteTotals(data).total;
  return {
    pixKey: data.company.pixKey,
    merchantName: (data.company.name || 'EMPRESA').slice(0, 25),
    merchantCity: data.company.pixCity || 'SAO PAULO',
    amount: amount > 0 ? amount : undefined,
    txId: `ORC${Date.now().toString().slice(-8)}`,
  };
};

/** Um BR Code → imagem PNG (data URL). `null` quando não há código ou o QR falha. */
export const renderPixQrFromCode = async (code: string | null): Promise<string | null> => {
  if (!code) return null;
  try {
    const QRCode = await import('qrcode');
    return await QRCode.toDataURL(code, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 200,
      color: { dark: '#000000', light: '#FFFFFF' },
    });
  } catch (qrError) {
    console.error('Error rendering PIX QR code:', qrError);
    return null;
  }
};

/** Gera o QR do PIX deste documento (ou `null`). */
export const buildPixQrDataUrl = async (data: PDFQuoteData): Promise<string | null> => {
  if (!pixQrEligible(data)) return null;

  if (dynamicPixIsUsable(data) && data.pixDynamic) {
    const { payload, qrBase64 } = data.pixDynamic;
    // A imagem do provedor tem precedência: é a mesma que o cliente vê na fatura.
    if (typeof qrBase64 === 'string' && qrBase64.trim()) {
      return `data:image/png;base64,${qrBase64.trim()}`;
    }
    return renderPixQrFromCode(payload);
  }

  const args = staticPixArgs(data);
  if (!args) return null;
  try {
    return await generatePixQrCodeDataUrl(args);
  } catch (qrError) {
    console.error('Error generating PIX QR code for PDF:', qrError);
    return null;
  }
};

export const renderPixSection = async (
  doc: jsPDF,
  data: PDFQuoteData,
  yPos: number,
  opts: PixSectionOptions,
): Promise<number> => {
  if (!pixQrEligible(data)) return yPos;
  const { primaryColor, grayText, darkText, margin, pageWidth, pageHeight } = opts;
  const dynamic = dynamicPixIsUsable(data) ? data.pixDynamic ?? null : null;
  const contentWidth = pageWidth - margin * 2;

  if (yPos > pageHeight - 100) {
    doc.addPage();
    yPos = 20;
  }

  const pixBoxHeight = 75;
  const qrCodeSize = 55;

  // Só para IMPRIMIR o valor no bloco; o número que vai dentro do QR é
  // calculado por `buildPixQrDataUrl`, a partir da mesma função.
  const pixAmount = pdfQuoteTotals(data).total;

  doc.setFillColor(245, 250, 255);
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, yPos, contentWidth, pixBoxHeight, 3, 3, 'FD');

  // Card icon
  const iconX = margin + 4;
  const iconY = yPos + 4;
  doc.setFillColor(...primaryColor);
  doc.roundedRect(iconX, iconY, 12, 8, 1, 1, 'F');
  doc.setFillColor(255, 255, 255);
  doc.rect(iconX, iconY + 2, 12, 2, 'F');
  doc.setFillColor(255, 215, 0);
  doc.rect(iconX + 2, iconY + 5, 2.5, 2, 'F');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Pagamento via Pix', iconX + 15, yPos + 10);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  doc.text(dynamic ? 'Pix copia e cola:' : 'Chave Pix:', margin + 4, yPos + 22);

  doc.setFont('courier', 'bold');
  doc.setTextColor(...darkText);
  if (dynamic) {
    // O BR Code de uma cobrança tem ~150–200 caracteres e NÃO cabe numa linha.
    // Sem a quebra, o jsPDF simplesmente desenha por cima do QR ao lado — não
    // avisa, não corta (é a mesma armadilha do #116 na bobina de 58 mm). A
    // largura útil exclui o QR, que ocupa a direita do bloco.
    const textWidth = contentWidth - qrCodeSize - 18;
    doc.setFontSize(6);
    const lines = doc.splitTextToSize(dynamic.payload, textWidth) as string[];
    // Teto de 4 linhas: o bloco tem altura fixa (`pixBoxHeight`) e o excedente
    // invadiria o valor logo abaixo. O código completo continua no `invoiceUrl`.
    doc.text(lines.slice(0, 4), margin + 4, yPos + 27);
  } else {
    doc.setFontSize(9);
    doc.text(data.company?.pixKey ?? '', margin + 4, yPos + 30);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...grayText);
  doc.text('Escaneie o QR Code ao lado para pagar', margin + 4, yPos + 42);

  if (pixAmount > 0) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text(`Valor: ${formatCurrency(pixAmount)}`, margin + 4, yPos + 52);
  }

  if (dynamic) {
    // ─── ATRIBUIÇÃO OBRIGATÓRIA AO ASAAS ────────────────────────────────────
    // Resolução Conjunta nº 16/2025: o cliente final tem que saber quem presta
    // o serviço financeiro em todo ponto de contato onde há movimentação de
    // valores — e um documento com QR de cobrança é exatamente isso.
    //
    // Só no DINÂMICO: no estático o QR é da chave Pix do próprio prestador,
    // pagamento direto entre ele e o cliente, sem o Asaas no meio. Creditar o
    // Asaas ali seria uma afirmação falsa (e o inverso do que a norma quer).
    //
    // Texto, não o selo: o PDF é gerado no cliente (#13), inclusive offline, e
    // uma imagem de CDN sairia como buraco no papel. A menção textual cumpre a
    // mesma exigência sem depender de rede.
    const disclosureWidth = contentWidth - qrCodeSize - 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...grayText);
    const disclosureLines = doc.splitTextToSize(ASAAS_PDF_DISCLOSURE, disclosureWidth) as string[];
    doc.text(disclosureLines.slice(0, 2), margin + 4, yPos + 62);
  }

  const qrDataUrl = await buildPixQrDataUrl(data);
  if (qrDataUrl) {
    const qrX = pageWidth - margin - qrCodeSize - 5;
    const qrY = yPos + 8;
    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrCodeSize, qrCodeSize);
  }

  return yPos + pixBoxHeight + 8;
};

// ============================================================================
// PHOTOS SECTION (shared across templates)
// ============================================================================

export interface PhotosSectionOptions {
  darkText: RGB;
  grayText: RGB;
  grayLight: RGB;
  margin: number;
  pageWidth: number;
  pageHeight: number;
  compact?: boolean;
}

export const renderPhotosSection = async (
  doc: jsPDF,
  data: PDFQuoteData,
  yPos: number,
  opts: PhotosSectionOptions,
): Promise<number> => {
  const { darkText, grayText, grayLight, margin, pageHeight, compact } = opts;

  if (compact) return yPos; // Compact template skips photos

  const itemsWithPhotos = data.items.filter((item) => item.image_url);
  const generalPhotos = data.generalPhotos || [];
  if (itemsWithPhotos.length === 0 && generalPhotos.length === 0) return yPos;

  if (yPos > pageHeight - 100) {
    doc.addPage();
    yPos = 20;
  }

  const photoSize = 55;
  const photosPerRow = 3;
  const photoGap = 8;
  const captionHeight = 12;

  if (itemsWithPhotos.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('Fotos dos itens', margin, yPos);
    yPos += 8;

    for (let i = 0; i < itemsWithPhotos.length; i++) {
      const item = itemsWithPhotos[i];
      const col = i % photosPerRow;
      const row = Math.floor(i / photosPerRow);

      if (row > 0 && col === 0) {
        yPos += photoSize + captionHeight + photoGap;
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = 20;
        }
      }

      const x = margin + col * (photoSize + photoGap);
      const y = yPos;

      if (item.image_url) {
        try {
          const imgData = await getDataUrl(item.image_url);
          if (imgData) {
            doc.addImage(imgData, 'JPEG', x, y, photoSize, photoSize);
          } else {
            doc.setFillColor(...grayLight);
            doc.rect(x, y, photoSize, photoSize, 'F');
          }
        } catch {
          doc.setFillColor(...grayLight);
          doc.rect(x, y, photoSize, photoSize, 'F');
        }
      }

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      const caption = doc.splitTextToSize(item.name || 'Item', photoSize)[0];
      doc.text(caption, x, y + photoSize + 5);
    }

    const totalItemRows = Math.ceil(itemsWithPhotos.length / photosPerRow);
    yPos += totalItemRows * (photoSize + captionHeight + photoGap) + 10;
  }

  if (generalPhotos.length > 0) {
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('Galeria do projeto', margin, yPos);
    yPos += 8;

    for (let i = 0; i < generalPhotos.length; i++) {
      const photo = generalPhotos[i];
      const col = i % photosPerRow;
      const row = Math.floor(i / photosPerRow);

      if (row > 0 && col === 0) {
        yPos += photoSize + captionHeight + 8 + photoGap;
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = 20;
        }
      }

      const x = margin + col * (photoSize + photoGap);
      const y = yPos;

      try {
        const imgData = await getDataUrl(photo.imageUrl);
        if (imgData) {
          doc.addImage(imgData, 'JPEG', x, y, photoSize, photoSize);
        } else {
          doc.setFillColor(...grayLight);
          doc.rect(x, y, photoSize, photoSize, 'F');
        }
      } catch {
        doc.setFillColor(...grayLight);
        doc.rect(x, y, photoSize, photoSize, 'F');
      }

      let captionY = y + photoSize + 4;
      if (photo.title) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        const title = doc.splitTextToSize(photo.title, photoSize)[0];
        doc.text(title, x, captionY);
        captionY += 4;
      }
      if (photo.description) {
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...grayText);
        const desc = doc.splitTextToSize(photo.description, photoSize)[0];
        doc.text(desc, x, captionY);
      }
    }
  }

  return yPos;
};

// ============================================================================
// SIGNATURE SECTION (shared)
// ============================================================================

export interface SignatureSectionOptions {
  darkText: RGB;
  grayText: RGB;
  margin: number;
  pageWidth: number;
  pageHeight: number;
  elegant?: boolean;
  /**
   * yPos em que o conteúdo do template terminou na página corrente. Usado só
   * quando há assinatura gravada: o bloco cresce ~18mm para cima e sem esse
   * valor não há como saber se ele invadiria o texto acima. jsPDF não tem
   * layout automático — a colisão sairia silenciosamente no PDF.
   */
  contentBottom?: number;
}

/** Altura da caixa reservada para a imagem da assinatura (mm). */
const SIG_IMG_BOX_H = 16;
/** Respiro entre a base da imagem e a linha de assinatura (mm). */
const SIG_IMG_GAP = 2;

/**
 * Data de um TIMESTAMP (ex.: `quote_approvals.approved_at`) no calendário
 * local. NÃO use `formatQuoteDate` aqui: ela corta a string no 'T', o que é
 * certo para uma coluna DATE mas devolve a data em UTC para um timestamptz —
 * uma aprovação às 22h48 de Brasília (01h48 UTC) sairia impressa no dia
 * seguinte. Pego em device real, num documento com valor probatório.
 */
const formatSignedAtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

/**
 * Bloco de assinaturas de orçamento/pedido/OS: "Data: __/__/____" e as duas
 * linhas ("Assinatura do cliente" / "Assinatura do responsável").
 *
 * Quando `data` traz assinatura, a imagem é desenhada ACIMA da linha
 * correspondente — a linha e os rótulos permanecem sempre (a imagem é um
 * reforço, não um substituto; se ela falhar ao carregar, o documento continua
 * assinável à mão). A linha fica ancorada em `pageHeight - 26` com ou sem
 * imagem: só o que está acima dela cresce, então o rodapé nunca se move.
 */
export const renderSignatureSection = async (
  doc: jsPDF,
  data: PDFQuoteData,
  opts: SignatureSectionOptions,
): Promise<void> => {
  const { darkText, grayText, margin, pageWidth, pageHeight, elegant, contentBottom } = opts;

  const signatureLineWidth = 70;
  const sig1X = margin + 20;
  const sig2X = pageWidth - margin - signatureLineWidth;
  const sigLineY = pageHeight - 26;

  const clientSig = data.clientSignatureDataUrl?.trim() || null;
  const providerSig = data.providerSignatureDataUrl?.trim() || null;
  // A reserva de espaço olha os DADOS, não o sucesso do download: assim o
  // layout não muda se a imagem falhar (degrada para a linha em branco).
  const hasSignature = Boolean(clientSig || providerSig);
  const imgTop = sigLineY - SIG_IMG_GAP - SIG_IMG_BOX_H;
  const dateY = sigLineY - 16 - (hasSignature ? SIG_IMG_BOX_H + SIG_IMG_GAP : 0);

  // Sem assinatura o bloco tem a altura de sempre e o comportamento histórico é
  // preservado byte a byte. Com assinatura ele sobe ~18mm e pode alcançar o
  // conteúdo: nesse caso empurra a seção inteira para a próxima página em vez
  // de sobrepor o texto.
  if (hasSignature && contentBottom !== undefined && contentBottom > dateY - 6) {
    doc.addPage();
  }

  doc.setDrawColor(...grayText);
  doc.setLineWidth(0.3);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  /*
   * LOCAL E DATA ocupam a MESMA linha que o "Data: ____/____/________" sempre
   * ocupou — o local de emissão SUBSTITUI a lacuna, não se soma a ela.
   *
   * Duas razões, e a segunda é a que decide: (a) semanticamente, um documento
   * que já declara "São Paulo, 9 de agosto de 2026" não pede a data à mão de
   * novo — imprimir os dois lado a lado é contraditório; (b) acrescentar uma
   * linha mudaria a altura do bloco em 11 templates que têm guardas de colisão
   * próprios (#13/#34). Trocando o texto, a geometria é EXATAMENTE a de sempre,
   * e quem não preenche a cidade recebe o documento byte a byte igual.
   */
  const issueLine = formatIssueLine(data.issuePlace, data.quoteDate);
  doc.text(issueLine || 'Data: ____/____/________', margin, dateY);

  /** Desenha a assinatura centrada na caixa, apoiada na linha, sem distorcer. */
  const drawSignatureImage = async (x: number, source: string): Promise<void> => {
    try {
      const img = await getDataUrl(source);
      if (!img) return;
      let width = signatureLineWidth;
      let height = SIG_IMG_BOX_H;
      try {
        const props = doc.getImageProperties(img);
        if (props?.width && props?.height) {
          const scale = Math.min(signatureLineWidth / props.width, SIG_IMG_BOX_H / props.height);
          width = props.width * scale;
          height = props.height * scale;
        }
      } catch {
        /* sem as dimensões, ocupa a caixa inteira */
      }
      doc.addImage(
        img,
        getJsPdfImageFormat(img),
        x + (signatureLineWidth - width) / 2,
        imgTop + (SIG_IMG_BOX_H - height),
        width,
        height,
      );
    } catch {
      /* segue sem a imagem — a linha em branco abaixo continua servindo */
    }
  };

  const renderSide = async (
    x: number,
    label: string,
    signature: string | null,
    signerName?: string | null,
    meta?: string | null,
  ): Promise<void> => {
    if (signature) await drawSignatureImage(x, signature);

    doc.line(x, sigLineY, x + signatureLineWidth, sigLineY);

    const centerX = x + signatureLineWidth / 2;
    const name = signerName?.trim();

    if (name) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      const nameLine = (doc.splitTextToSize(name, signatureLineWidth) as string[])[0];
      doc.text(nameLine, centerX, sigLineY + 4.5, { align: 'center' });

      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayText);
      doc.text(meta ? `${label} · ${meta}` : label, centerX, sigLineY + 8.5, { align: 'center' });
      return;
    }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);
    doc.text(label, centerX, sigLineY + 5, { align: 'center' });

    if (meta) {
      doc.setFontSize(6.5);
      doc.text(meta, centerX, sigLineY + 9, { align: 'center' });
    }
  };

  // A data da aprovação é o que dá valor probatório à assinatura do cliente.
  // Depende só de haver data: `approve_quote_public` aceita assinatura nula, e
  // nesse caso nome + data continuam valendo sobre a linha em branco.
  // Na OS o gesto é OUTRO: o cliente assina o RECEBIMENTO do serviço, não a
  // aprovação de uma proposta. Imprimir "Aprovado em" no comprovante de entrega
  // descreveria um ato que não aconteceu.
  const approvedMeta = data.clientSignedAt
    ? `${data.documentType === 'ordem_servico' ? 'Assinado' : 'Aprovado'} em ${formatSignedAtDate(data.clientSignedAt)}`
    : null;

  // A linha do cliente pode ser desligada no PDV (venda de balcão já paga não
  // costuma ser assinada). A do RESPONSÁVEL fica: ela autentica quem emitiu.
  if (showsPdvClientSignature(data)) {
    await renderSide(sig1X, 'Assinatura do cliente', clientSig, data.clientSignerName, approvedMeta);
  }
  await renderSide(sig2X, 'Assinatura do responsável', providerSig, data.providerSignerName, null);

  if (elegant) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...grayText);
    // Com imagem, o rótulo sobe para não ficar por baixo dela.
    const deAcordoY = hasSignature ? imgTop - 2 : sigLineY - 3;
    doc.text('De acordo:', sig1X, deAcordoY);
    doc.text('De acordo:', sig2X, deAcordoY);
  }
};

// ============================================================================
// FOOTER (shared branding + sequence number)
// ============================================================================

export interface FooterOptions {
  primaryColor: RGB;
  grayText: RGB;
  margin: number;
  pageWidth: number;
  pageHeight: number;
  /**
   * Primeira página que pertence a ESTE documento (1-based). Default 1.
   *
   * Existe por causa da exportação de vários recibos num PDF só: o rodapé
   * carrega o número do documento (`#0042`), e o laço abaixo percorre TODAS as
   * páginas. Sem o recorte, ao renderizar o 2º recibo o rodapé dele seria
   * desenhado também sobre a página do 1º — dois números sobrepostos no mesmo
   * ponto, ilegíveis, num documento que o usuário arquiva.
   */
  fromPage?: number;
}

export const renderFooter = (
  doc: jsPDF,
  data: PDFQuoteData,
  opts: FooterOptions,
): void => {
  const { primaryColor, grayText, margin, pageWidth, pageHeight, fromPage } = opts;
  const totalPages = doc.getNumberOfPages();
  const businessNameForFooter = data.company?.name || 'Minha Empresa';

  for (let i = Math.max(1, fromPage ?? 1); i <= totalPages; i++) {
    doc.setPage(i);
    const footerY = pageHeight - 8;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');

    if (!data.isPremium) {
      doc.setTextColor(...grayText);
      const brandingText = 'Gerado por Orçamigo - Crie o seu em ';
      doc.text(brandingText, margin, footerY);

      const textWidth = doc.getTextWidth(brandingText);
      doc.setTextColor(...primaryColor);
      doc.textWithLink('orcamigo.com.br', margin + textWidth, footerY, {
        url: 'https://orcamigo.com.br',
      });
    } else {
      doc.setTextColor(...grayText);
      doc.text(businessNameForFooter, margin, footerY);
    }

    if (data.showSequenceId && data.sequenceId) {
      const sequenceText = `#${String(data.sequenceId).padStart(4, '0')}`;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayText);
      doc.text(sequenceText, pageWidth - margin, footerY, { align: 'right' });
    }
  }
};

// ============================================================================
// STANDARD COLOR PALETTE (used by most templates)
// ============================================================================

export const buildPalette = (brandColorRGB?: RGB) => ({
  primaryColor: (brandColorRGB || [16, 185, 129]) as RGB,
  grayVeryLight: [249, 250, 251] as RGB,
  grayLight: [229, 231, 235] as RGB,
  grayText: [75, 85, 99] as RGB,
  darkText: [17, 24, 39] as RGB,
  white: [255, 255, 255] as RGB,
});
